/**
 * Frontend human-in-the-loop tool for presenting a proposed plan.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools
 *   2. Sets status to "requires-action" for human input
 *   3. Enables addResult() to send the user's approval/feedback back
 *
 * Shows a numbered list of plan steps. The user can approve or
 * request changes with an optional feedback message.
 */
import { useState, useMemo } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ToolCard } from "./ToolCard";

interface PlanStep {
  id: string;
  title: string;
  description?: string;
}

interface PlanArgs {
  title: string;
  description: string;
  steps: string;
}

function parseSteps(raw: unknown): PlanStep[] | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is PlanStep =>
        typeof item === "object" &&
        item !== null &&
        typeof item.id === "string" &&
        typeof item.title === "string"
    );
  } catch {
    return null;
  }
}

const PlanRender: ToolCallMessagePartComponent<PlanArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  const steps = useMemo(() => (args ? parseSteps(args.steps) : null), [args]);

  const isRunning = status.type === "running";
  const isSubmitted = result != null;

  const handleApprove = () => {
    addResult(
      "The user approved the plan. Proceed with execution and respond with text only. Do NOT call any tool."
    );
  };

  const handleRequestChanges = () => {
    if (!showFeedback) {
      setShowFeedback(true);
      return;
    }
    const msg = feedback.trim()
      ? `The user requested changes to the plan. Feedback: ${feedback.trim()}.`
      : "The user requested changes to the plan (no specific feedback given).";
    addResult(`${msg} Respond with text only. Do NOT call any tool.`);
  };

  // Submitted state
  if (isSubmitted && steps) {
    const wasApproved = typeof result === "string" && result.startsWith("The user approved");
    return (
      <ToolCard label="Plan" color="indigo" isLoading={false} loadingText="" isSubmitted={true}>
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        <ol className="space-y-1.5 pl-0">
          {steps.map((step, i) => (
            <li key={step.id} className="flex items-start gap-2 text-sm">
              <span className="text-indigo-400 font-medium shrink-0">{i + 1}.</span>
              <span className={cn(wasApproved ? "text-gray-700" : "text-gray-400")}>
                {step.title}
              </span>
            </li>
          ))}
        </ol>
        <p className="text-xs font-medium">
          {wasApproved ? (
            <span className="text-green-600">Approved</span>
          ) : (
            <span className="text-amber-600">Changes requested</span>
          )}
        </p>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Plan"
      color="indigo"
      isLoading={isRunning || !steps}
      loadingText="Generating plan..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && <p className="text-xs text-muted-foreground">{args.description}</p>}

      <ol className="space-y-2 pl-0">
        {steps?.map((step, i) => (
          <li key={step.id} className="flex items-start gap-2.5">
            <span className="text-indigo-500 font-semibold text-sm shrink-0 mt-0.5">{i + 1}.</span>
            <div>
              <p className="text-sm font-medium">{step.title}</p>
              {step.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {showFeedback && (
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What changes would you like? (optional)"
          rows={2}
          className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent resize-none"
        />
      )}

      <div className="flex items-center gap-2 mt-1">
        <Button size="sm" onClick={handleApprove}>
          <Check className="size-3.5 mr-1" />
          Approve
        </Button>
        <Button size="sm" variant="outline" onClick={handleRequestChanges}>
          <X className="size-3.5 mr-1" />
          {showFeedback ? "Submit Changes" : "Request Changes"}
        </Button>
      </div>
    </ToolCard>
  );
};

// Intersection with Record<string, unknown> needed because makeAssistantTool's
// generic constraint requires an index signature that interfaces don't provide.
export const PlanTool = makeAssistantTool<PlanArgs & Record<string, unknown>, string>({
  toolName: "present_plan",
  type: "human",
  description:
    "Present a proposed plan with numbered steps for the user to review. " +
    "The user can approve the plan or request changes with optional feedback.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", description: "Title for the proposed plan" },
      description: {
        type: "string",
        description: "Context about what this plan accomplishes",
      },
      steps: {
        type: "string",
        description:
          'JSON array of step objects, each with: id (string), title (string), description (optional string). Example: [{"id":"1","title":"Fetch data","description":"Query the database for active users"}]',
      },
    },
    required: ["title", "steps"],
  },
  render: PlanRender,
});
